import React, { useState, useEffect, useCallback } from 'react';
import { Agent } from '../types';
import {
  listAgents,
  listAllThreads,
  getAgentThread,
  MastraThreadWithAgent,
  MastraThreadMessage,
} from '../api/client';
import { MarkdownText } from './MarkdownText';

// Cross-agent view of Mastra Memory conversations (threads written by the
// standard /api/std drop-in). Separate data model from platform Sessions.
export function ConversationsPage({ embedded = false }: { embedded?: boolean } = {}) {
  const [threads, setThreads] = useState<MastraThreadWithAgent[]>([]);
  const [agents, setAgents] = useState<Record<string, Agent>>({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<MastraThreadMessage[]>([]);
  const [msgsLoading, setMsgsLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const agentsData = await listAgents();
      const agentMap: Record<string, Agent> = {};
      agentsData.forEach((a) => { agentMap[a.id] = a; });
      setAgents(agentMap);
      setThreads(await listAllThreads(agentsData.map((a) => a.id)));
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const open = async (t: MastraThreadWithAgent) => {
    if (selected === t.id) { setSelected(null); return; }
    setSelected(t.id);
    setMsgs([]);
    setMsgsLoading(true);
    try {
      const r = await getAgentThread(t.agentId, t.id);
      setMsgs(r.messages);
    } catch {
      setMsgs([]);
    } finally {
      setMsgsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        {embedded ? <span /> : <h2 style={styles.heading}>Conversations</h2>}
        <button style={styles.refreshBtn} onClick={load}>Refresh</button>
      </div>

      {loading ? (
        <div style={styles.muted}>Loading...</div>
      ) : threads.length === 0 ? (
        <div style={styles.muted}>No Mastra conversations yet (created via /api/std).</div>
      ) : (
        <div style={styles.list}>
          {threads.map((t) => {
            const agentName = agents[t.agentId]?.name || t.agentId.slice(0, 8);
            const isOpen = selected === t.id;
            return (
              <div key={t.id} style={styles.card}>
                <div style={styles.cardHeader} onClick={() => open(t)}>
                  <div style={styles.cardLeft}>
                    <span style={styles.agentName}>{agentName}</span>
                    <code style={styles.threadId}>{t.title || t.id}</code>
                  </div>
                  <div style={styles.cardRight}>
                    <span style={styles.date}>{t.updatedAt ? new Date(t.updatedAt).toLocaleString() : ''}</span>
                    <span style={styles.chevron}>{isOpen ? '▲' : '▼'}</span>
                  </div>
                </div>
                {isOpen && (
                  <div style={styles.messages}>
                    {msgsLoading ? (
                      <div style={styles.muted}>Loading...</div>
                    ) : msgs.length === 0 ? (
                      <div style={styles.muted}>No messages</div>
                    ) : (
                      msgs.map((m, i) => (
                        <div key={i} style={styles.message}>
                          <div style={{
                            ...styles.messageRole,
                            color: m.role === 'user' ? '#2563eb' : '#16a34a',
                          }}>
                            {m.role}
                          </div>
                          <div style={styles.messageText}>
                            {m.role === 'assistant' ? <MarkdownText text={m.text} /> : m.text}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: '20px', flex: 1, overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  heading: { margin: 0, fontSize: 18, color: '#1a1a1a' },
  refreshBtn: { padding: '4px 12px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', fontSize: 12, cursor: 'pointer', color: '#555' },
  muted: { color: '#888', fontSize: 14 },
  list: { display: 'flex', flexDirection: 'column', gap: 8 },
  card: { border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', cursor: 'pointer', background: '#fafafa' },
  cardLeft: { display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 },
  cardRight: { display: 'flex', alignItems: 'center', gap: 8 },
  agentName: { fontSize: 13, fontWeight: 600, color: '#1a1a1a' },
  threadId: { fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  date: { fontSize: 11, color: '#aaa' },
  chevron: { fontSize: 10, color: '#aaa' },
  messages: { padding: '12px 16px', background: '#fff', borderTop: '1px solid #e5e7eb' },
  message: { padding: '6px 0', borderBottom: '1px solid #f3f4f6' },
  messageRole: { fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, marginBottom: 2 },
  messageText: { fontSize: 13, color: '#1a1a1a', lineHeight: 1.4 },
};
